import DarkMode from "@mui/icons-material/DarkMode";
import WbSunny from "@mui/icons-material/WbSunny";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { INK_BLACK, SHIFT_DAY, SHIFT_NIGHT_FALLBACK } from "../../../theme/tokens";

/** The legacy night slot — every other slot index reads as a day shift. */
const NIGHT_SHIFT_INDEX = 1;

type Props = {
	/** Legacy day (0) / night (1) slot of the window (`getShiftIndexForWindow`). */
	shiftIndex: number;
	/**
	 * Announce the glyph ("Day shift") where it carries meaning on its own; leave off where the window's
	 * hours sit right beside it and a wrapper already hides it from assistive tech.
	 */
	labelled?: boolean;
	/** Overrides the default `shift-window-icon-{day|night}` test id. */
	dataTestId?: string;
};

/**
 * The sun / moon that marks a shift window wherever one is named — Add Staff's cards, step header and
 * footer; Remove Staff's filter pills and group bands — sized by the surrounding text (`fontSize: inherit`).
 * The hospital demo shows it in dark ink like the rest of its page.
 */
// DEFAULT: `labelled` off — most placements have the window's hours right beside the glyph, so it is decoration.
export const ShiftWindowIcon: FC<Props> = ({ shiftIndex, labelled = false, dataTestId }) => {
	const { t } = useTranslation();
	const { isHospitalView } = useHospitalData();
	const isNight = shiftIndex === NIGHT_SHIFT_INDEX;
	const Icon = isNight ? DarkMode : WbSunny;
	const color = isHospitalView ? INK_BLACK : isNight ? SHIFT_NIGHT_FALLBACK : SHIFT_DAY;
	const label = t(isNight ? "shift.window.icon.night" : "shift.window.icon.day");
	return (
		<Icon
			sx={{ color, fontSize: "inherit" }}
			{...(labelled ? { titleAccess: label, role: "img" } : { "aria-hidden": true })}
			// DEFAULT: without an override the id names the glyph drawn, which is what most placements assert on.
			data-testid={dataTestId ?? `shift-window-icon-${isNight ? "night" : "day"}`}
		/>
	);
};
